import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { AppDataService } from '../../core/services/app-data.service';
import { DigitsOnlyDirective } from '../../core/directives';
import { sanitizeDigits } from '../../core/utils/input-validation.util';

@Component({
  selector: 'app-otp-page',
  standalone: true,
  imports: [CommonModule, DigitsOnlyDirective],
  templateUrl: './otp-page.component.html',
  styleUrls: ['./otp-page.component.css'],
})
export class OtpPageComponent implements OnInit, OnDestroy {
  private auth = inject(AuthService);
  private router = inject(Router);
  private appData = inject(AppDataService);

  maskedEmail = '';
  demoOtp = '';
  showError = false;
  errorMessage = '';
  showToast = false;
  toastMessage = 'Code verified successfully';
  resendDisabled = false;
  timer = 60;
  isVerifying = false;
  private timerInterval: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.maskedEmail = this.auth.getMaskedEmail();
    this.demoOtp = this.auth.demoOtp;
    this.startTimer();
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  onOtpInput(event: Event, nextInputId: string): void {
    const input = event.target as HTMLInputElement;
    const value = sanitizeDigits(input.value, 1);
    if (input.value !== value) {
      input.value = value;
    }

    if (!value) {
      return;
    }

    if (nextInputId) {
      const nextInput = document.getElementById(
        nextInputId,
      ) as HTMLInputElement | null;
      nextInput?.focus();
    }

    this.checkOtpComplete();
  }

  onOtpKeydown(event: KeyboardEvent, currentId: string, prevId: string): void {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace' && input.value === '' && prevId) {
      const prevInput = document.getElementById(
        prevId,
      ) as HTMLInputElement | null;
      if (prevInput) {
        prevInput.focus();
        prevInput.value = '';
      }
    }
  }

  getOtpValue(): string {
    let otp = '';
    for (let i = 1; i <= 6; i++) {
      const input = document.getElementById(
        `otp${i}`,
      ) as HTMLInputElement | null;
      if (input?.value) {
        otp += input.value;
      }
    }
    return otp;
  }

  checkOtpComplete(): void {
    if (this.getOtpValue().length === 6) {
      this.verifyCode();
    }
  }

  verifyCode(): void {
    if (this.isVerifying) {
      return;
    }

    const otp = this.getOtpValue();

    if (otp.length !== 6) {
      this.showErrorMessage('Please enter all 6 digits.');
      return;
    }

    this.isVerifying = true;
    this.auth.verifyOtp(otp).subscribe({
      next: (result) => {
        this.isVerifying = false;

        if (!result.success) {
          this.showErrorMessage(result.message ?? 'Invalid verification code.');
          return;
        }

        this.toastMessage = 'Code verified successfully';
        this.showToast = true;
        this.appData.refreshAll();

        setTimeout(() => {
          this.showToast = false;
          this.router.navigate(['/dashboard']);
        }, 1200);
      },
      error: () => {
        this.isVerifying = false;
        this.showErrorMessage('Invalid verification code.');
      },
    });
  }

  resendCode(): void {
    if (this.resendDisabled) {
      return;
    }

    this.auth.resendOtp().subscribe({
      next: (result) => {
        if (!result.success) {
          this.showErrorMessage(
            result.message ?? 'Unable to resend code. Please sign in again.',
          );
          return;
        }

        this.demoOtp = this.auth.demoOtp;
        this.resendDisabled = true;
        this.timer = 60;
        this.startTimer();
        this.clearOtpInputs();

        this.toastMessage = 'OTP code resent successfully';
        this.showToast = true;
        setTimeout(() => {
          this.showToast = false;
        }, 3000);
      },
      error: () => {
        this.showErrorMessage('Unable to resend code. Please sign in again.');
      },
    });
  }

  startTimer(): void {
    this.clearTimer();

    this.timerInterval = setInterval(() => {
      this.timer--;
      if (this.timer <= 0) {
        this.timer = 0;
        this.resendDisabled = false;
        this.clearTimer();
      }
    }, 1000);
  }

  goBack(): void {
    this.auth.clearPendingOtp();
    this.router.navigate(['/login']);
  }

  closeToast(): void {
    this.showToast = false;
  }

  private showErrorMessage(message: string): void {
    this.showError = true;
    this.errorMessage = message;
    setTimeout(() => {
      this.showError = false;
    }, 3000);
  }

  private clearOtpInputs(): void {
    for (let i = 1; i <= 6; i++) {
      const input = document.getElementById(
        `otp${i}`,
      ) as HTMLInputElement | null;
      if (input) {
        input.value = '';
      }
    }
    document.getElementById('otp1')?.focus();
  }

  private clearTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }
}
